import { LANE_COUNT, type Heat, type TournamentState } from '@sprinters/shared';
import { formatTime } from '../../format';
import { h, replaceChildren } from '../dom';

const STATUS_LABELS: Readonly<Record<Heat['status'], string>> = {
  pending: 'À venir',
  live: 'En direct',
  done: 'Terminée',
};

/** Tableau du tournoi : un onglet par tour, une carte par série. */
export class Bracket {
  readonly element: HTMLElement;

  private readonly tabs = h('div', { class: 'bracket-tabs', role: 'tablist' });
  private readonly grid = h('div', { class: 'bracket-grid' });
  private selectedRound: number | null = null;
  private lastCurrentRound = -1;

  constructor(private readonly onHeatClick: (heat: Heat) => void) {
    this.element = h('div', { class: 'bracket' }, h('h2', null, 'Tableau'), this.tabs, this.grid);
  }

  update(
    tournament: TournamentState,
    myEntrantId: string | null,
    focusHeatId: string | null,
  ): void {
    // Quand le tournoi change de tour, l'onglet suit automatiquement.
    if (tournament.roundIndex !== this.lastCurrentRound) {
      this.lastCurrentRound = tournament.roundIndex;
      this.selectedRound = null;
    }
    const shownRound = this.selectedRound ?? tournament.roundIndex;

    replaceChildren(
      this.tabs,
      ...tournament.rounds.map((round) =>
        h(
          'button',
          {
            type: 'button',
            role: 'tab',
            class: `tab${round.index === shownRound ? ' is-active' : ''}${round.index === tournament.roundIndex ? ' is-current' : ''}`,
            'aria-selected': round.index === shownRound,
            onClick: () => {
              this.selectedRound = round.index;
              this.update(tournament, myEntrantId, focusHeatId);
            },
          },
          round.name,
        ),
      ),
    );

    const round = tournament.rounds[shownRound];
    replaceChildren(
      this.grid,
      ...(round?.heats ?? []).map((heat) => this.renderHeat(heat, myEntrantId, focusHeatId)),
    );
  }

  private renderHeat(
    heat: Heat,
    myEntrantId: string | null,
    focusHeatId: string | null,
  ): HTMLElement {
    const isMine = heat.lanes.some((lane) => lane.id === myEntrantId);
    const classes = ['heat-card', `is-${heat.status}`];
    if (isMine) classes.push('is-mine');
    if (heat.id === focusHeatId) classes.push('is-focus');

    const rows =
      heat.lanes.length === 0
        ? [
            h(
              'li',
              { class: 'heat-empty' },
              heat.roundIndex === 0 ? 'Tirage au sort…' : 'Qualifiés des séries précédentes',
            ),
          ]
        : Array.from({ length: LANE_COUNT }, (_, lane) => {
            const entrant = heat.lanes[lane];
            if (!entrant) return null;
            const result = heat.results?.find((candidate) => candidate.entrantId === entrant.id);
            return h(
              'li',
              {
                class: `heat-lane${entrant.id === myEntrantId ? ' is-me' : ''}${result?.qualified ? ' is-qualified' : ''}${result && !result.qualified ? ' is-out' : ''}`,
              },
              h('span', { class: 'lane-no' }, lane + 1),
              h(
                'span',
                { class: `lane-name${entrant.kind === 'bot' ? ' is-bot' : ''}` },
                entrant.name,
              ),
              result &&
                h(
                  'span',
                  { class: 'lane-result' },
                  `${result.rank}ᵉ · ${formatTime(result.timeMs)}`,
                ),
            );
          });

    return h(
      'button',
      {
        type: 'button',
        class: classes.join(' '),
        disabled: heat.lanes.length === 0,
        onClick: () => this.onHeatClick(heat),
        title: heat.status === 'live' ? 'Regarder cette série' : 'Voir le détail',
      },
      h(
        'span',
        { class: 'heat-header' },
        h('strong', null, `Série ${heat.index + 1}`),
        isMine && h('span', { class: 'badge' }, 'Vous'),
        h('span', { class: `chip chip-${heat.status}` }, STATUS_LABELS[heat.status]),
      ),
      h('ul', { class: 'heat-lanes' }, ...rows),
    );
  }
}
